import type { LaptopInformation, Seller } from "./types";
import { amazonDpUrl, withAmazonTag } from "./utils";
import { conditionBadge, isRefurbished } from "./condition";

export function normCurrencySymbol(raw: string | undefined): string {
  if (!raw) return "£";
  if (raw === "GBP" || raw === "GPB" || raw === "£") return "£";
  if (raw === "USD" || raw === "$") return "$";
  return raw;
}

export type AmazonTarget = { url: string; price: number | null; sym: string };

/**
 * The currency the given market's prices are quoted in, or null for a country
 * we don't serve — in which case there is no expectation to enforce and the
 * data's own currency stands.
 */
function expectedSymbol(country: string | undefined): string | null {
  if (country === "us") return "$";
  if (country === "gb") return "£";
  return null;
}

/**
 * Whether a price denominated in `sym` may be quoted to someone in `country`.
 *
 * Prices are scraped from listings and carry their own currency, which is not
 * always the currency of the market being served: a config with no US listing
 * still has its GB Amazon record attached, so a US visitor would otherwise be
 * shown a pound price. Relabelling that number with a dollar sign would be
 * worse than showing nothing — it would assert a US price nobody quoted — so a
 * mismatch is treated as "no price we can stand behind". Every caller already
 * renders that state, since most configs have no price on file at all.
 */
function quotableIn(sym: string, country: string | undefined): boolean {
  const expected = expectedSymbol(country);
  return expected === null || sym === expected;
}

/** Drop a price that contradicts the market being served, keeping the link. */
function withQuotablePrice(
  target: AmazonTarget,
  country: string | undefined,
): AmazonTarget {
  if (target.price == null) return target;
  return quotableIn(target.sym, country) ? target : { ...target, price: null };
}

/**
 * One money formatter for the whole app, so the same price cannot render as
 * "£1,200" in one place and "£1,200.17" in another. Pence are shown only when
 * they are non-zero.
 */
export function formatMoney(price: number, sym: string, locale = "en-GB"): string {
  const hasPence = Math.round(price * 100) % 100 !== 0;
  return `${sym}${price.toLocaleString(locale, {
    minimumFractionDigits: hasPence ? 2 : 0,
    maximumFractionDigits: hasPence ? 2 : 0,
  })}`;
}

/**
 * The price to quote for this laptop.
 *
 * It is the price of the listing we actually send the user to, so the number we
 * print never contradicts the button they click. That means the Amazon target
 * first (the primary CTA everywhere), and only then the cheapest seller — which
 * is what `laptop.amazon_price_pounds` alone cannot give us, since it is null for
 * most of the catalog. Null when nothing anywhere carries a price, and also when
 * the only price on file is in another market's currency (see quotableIn).
 * Amazon listings that are a different model (see sellerMatchesLaptop) are not
 * quoted either, for the same reason the button will not link to them.
 *
 * `refurbished` is set when the quoted price comes from a used listing, so callers
 * can label it. The Amazon branch is always new, so it is left unset there.
 */
export function displayPrice(
  item: LaptopInformation,
  country?: string,
): { price: number; sym: string; refurbished?: boolean; conditionLabel?: string | null } | null {
  const target = getAmazonTarget(item, country);
  if (target?.price != null && target.price > 0) {
    return { price: target.price, sym: target.sym };
  }
  const cheapest = cheapestSellerPrice(item, { sameModelOnly: true });
  return cheapest && quotableIn(cheapest.sym, country) ? cheapest : null;
}

/**
 * The lowest price any seller lists. This — not the linked price — is what the
 * backend's `within_budget` flag is derived from, so copy that talks about the
 * budget has to be able to see it. `sameModelOnly` skips Amazon listings for a
 * different model, for when the number is quoted as this laptop's price.
 */
export function cheapestSellerPrice(
  item: LaptopInformation,
  options: { sameModelOnly?: boolean } = {},
): {
  price: number;
  sym: string;
  sellerName: string;
  refurbished: boolean;
  conditionLabel: string | null;
} | null {
  let best: Seller | null = null;
  let bestPrice = Infinity;
  for (const s of item.sellers ?? []) {
    const p = s.item_price ?? s.total_price;
    if (p == null || p <= 0) continue;
    if (options.sameModelOnly && isAmazonListing(s) && !sellerMatchesLaptop(s, item)) continue;
    if (p < bestPrice) {
      best = s;
      bestPrice = p;
    }
  }
  if (!best || bestPrice === Infinity) return null;

  return {
    price: bestPrice,
    sym: normCurrencySymbol(best.currency),
    sellerName: best.seller_name || best.vendor_name || "another seller",
    // Refurbished stock only reaches here when the user opted in, but the copy
    // still has to say so — a price the reader assumes is for a new machine is
    // the one thing this must never do.
    refurbished: isRefurbished(best),
    conditionLabel: conditionBadge(best),
  };
}

/** The seller's own link, falling back to a product URL built from its ASIN. */
export function sellerUrl(seller: Seller, country?: string): string | null {
  return withAmazonTag(
    seller.seller_url || (seller.amazon_asin ? amazonDpUrl(seller.amazon_asin, country) : null),
    country,
  );
}

/**
 * The part of a CPU name that tells it apart from its siblings, squashed to
 * lowercase letters and digits: "Intel Core i9-14900HX" -> "i914900hx",
 * "AMD Ryzen 9 8940HX" -> "8940hx", "Apple M4 (10-core CPU)" -> "m4".
 */
function cpuModelToken(cpuName: string | undefined): string | null {
  const words = String(cpuName ?? "")
    .replace(/\([^)]*\)/g, " ")
    .split(/\s+/)
    .filter((word) => /\d/.test(word));
  const token = (words[words.length - 1] ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return token.length >= 2 ? token : null;
}

/**
 * Whether a seller's listing is plausibly this laptop, judged by its title
 * naming the same CPU. Sellers are matched to a config loosely upstream, so the
 * list for an "MSI Crosshair A16 HX (Ryzen 9 8940HX)" can lead with an Intel
 * Crosshair 16 HX: same family, different machine. The CPU is the one spec every
 * Amazon title carries, and it is what separates those siblings.
 */
/** A seller that came from an Amazon search, the source of the loose matches. */
function isAmazonListing(seller: Seller): boolean {
  return seller.seller_name === "Amazon" || Boolean(seller.amazon_asin);
}

export function sellerMatchesLaptop(seller: Seller, item: LaptopInformation): boolean {
  const token = cpuModelToken(item.cpu?.name);
  if (!token) return false;
  const title = String(seller.title ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return title.includes(token);
}

// The Buy-on-Amazon button links to an Amazon listing and shows that listing's
// price. It prefers the laptop config's own Amazon product id (ASIN), reading
// the price only from a record describing that same ASIN so the link and price
// never disagree. Most configs have no ASIN, so it then falls back to the first
// Amazon product among the sellers — wiring that seller's link and price to the
// same button. Returns null (no button) only when neither source exists.
//
// That fallback only takes a seller whose listing names this laptop's CPU (see
// sellerMatchesLaptop). Most Amazon sellers on file are a sibling model, and a
// button that sends someone to a different machine is worse than no button.
export function getAmazonTarget(
  item: LaptopInformation,
  country?: string,
): AmazonTarget | null {
  const asin = item.laptop.amazon_product_id;
  const url = asin ? withAmazonTag(amazonDpUrl(asin, country), country) : null;

  if (asin && url) {
    // Price for this exact ASIN, from whichever record describes it.
    const desc = item.amazon_product_desc?.[0];
    if (desc && desc.asin === asin && desc.final_price > 0) {
      const sym = desc.currency === "USD" || desc.currency === "$" ? "$" : "£";
      return withQuotablePrice({ url, price: desc.final_price, sym }, country);
    }
    // The laptop-level Amazon price describes this same listing.
    const own = item.laptop.amazon_price_pounds;
    if (own != null && own > 0) {
      return withQuotablePrice({ url, price: own, sym: "£" }, country);
    }
    // Otherwise the scraped Amazon seller carrying this exact ASIN.
    const seller = (item.sellers ?? []).find(
      (s) => s.seller_name === "Amazon" && s.amazon_asin === asin && s.item_price != null,
    );
    if (seller?.item_price != null) {
      return withQuotablePrice(
        { url, price: seller.item_price, sym: normCurrencySymbol(seller.currency) },
        country,
      );
    }

    // ASIN present but no price tied to it — still show the button, without a price.
    return { url, price: null, sym: "£" };
  }

  // No config ASIN: fall back to the first Amazon product in the sellers list
  // that is this same model (sellers are pre-ranked, so this is the best
  // matching Amazon option). Its own link and price drive the button.
  const amazonSeller = (item.sellers ?? []).find(
    (s) => isAmazonListing(s) && (s.seller_url || s.amazon_asin) && sellerMatchesLaptop(s, item),
  );
  if (amazonSeller) {
    const url = sellerUrl(amazonSeller, country);
    if (url) {
      const price = amazonSeller.item_price ?? amazonSeller.total_price ?? null;
      return withQuotablePrice(
        { url, price, sym: normCurrencySymbol(amazonSeller.currency) },
        country,
      );
    }
  }

  return null;
}
